import React from "react";

import Button from "@busbud/design-system-components/dist/Button";
import Dialog from "@busbud/design-system-components/dist/Dialog";
import DialogActions from "@busbud/design-system-components/dist/DialogActions";
import DialogContent from "@busbud/design-system-components/dist/DialogContent";
import DialogTitle from "@busbud/design-system-components/dist/DialogTitle";
import Divider from "@busbud/design-system-components/dist/Divider";
import Stack from "@busbud/design-system-components/dist/Stack";
import Text from "@busbud/design-system-components/dist/Text";

import type { ErrorModalButtonType } from "@app/helpers/api-error";
import { useAppContext } from "@app/helpers/hooks";

import { useDialogStyles, useDialogTitleStyles } from "./alert-dialog.styles";
import { Markdown } from "./markdown";

const BUTTON_TYPES = {
  CALL_TO_ACTION: "cta",
  PRIMARY: "primary",
  SECONDARY: "secondary"
} as const satisfies Record<string, ErrorModalButtonType>;

const COLOR = {
  [BUTTON_TYPES.CALL_TO_ACTION]: "accent",
  [BUTTON_TYPES.PRIMARY]: "primary",
  [BUTTON_TYPES.SECONDARY]: "tertiary"
} as const;

type ButtonConfig = {
  label: string;
  props?: Object;
  action_name?: string | null;
};

type AlertTitleProps = {
  title?: string | false;
  onClose?: () => void;
  classes?: Partial<Record<"root", string>>;
};

const AlertTitle: React.FC<AlertTitleProps> = props => {
  const { title, classes: external_classes, ...other } = props;
  const { classes: dialog_title_classes } = useDialogTitleStyles();
  const classes = external_classes || dialog_title_classes;

  const { translator } = useAppContext();

  if (props.title === false) {
    return null;
  }

  const defaulted_title = title || translator.t("!route.errors.default-title");

  return (
    <DialogTitle
      classes={{
        root: classes.root
      }}
      {...other}
    >
      <span>{defaulted_title}</span>
    </DialogTitle>
  );
};

type AlertBodyProps = {
  message?: React.ReactNode;
  tip?: string;
  classes?: Partial<Record<"content" | "message", string>>;
  classNames?: string;
};

const AlertBody: React.FC<AlertBodyProps> = props => {
  const { translator } = useAppContext();
  const { classes: external_classes, classNames } = props;
  const { cx, classes: dialog_title_classes } = useDialogStyles();
  const classes = external_classes || dialog_title_classes;

  const message = React.isValidElement(props.message) ? (
    props.message
  ) : (
    <Markdown
      className={cx("alert-markdown", classNames)}
      source={
        typeof props.message === "string"
          ? props.message
          : translator.t("!route.errors.default-text")
      }
    />
  );

  const tip = props.tip ? <Text size="sm">{props.tip}</Text> : null;

  return (
    <DialogContent className={classes.content}>
      <Stack direction="column" spacing={1}>
        {message}
        {tip}
      </Stack>
    </DialogContent>
  );
};

type AlertFooterProps = {
  handleButtonClick: (
    button_type: ErrorModalButtonType,
    action_name?: string | null
  ) => void;
  cta_button?: ButtonConfig;
  primary_button?: ButtonConfig;
  secondary_button?: ButtonConfig;
};

const AlertFooter: React.FC<AlertFooterProps> = props => {
  const { cta_button, primary_button, secondary_button, handleButtonClick } =
    props;

  if (cta_button || primary_button || secondary_button) {
    return (
      <DialogActions direction="vertical">
        <AlertButton
          type={BUTTON_TYPES.CALL_TO_ACTION}
          config={cta_button}
          onClick={handleButtonClick}
        />
        <AlertButton
          type={BUTTON_TYPES.PRIMARY}
          config={primary_button}
          onClick={handleButtonClick}
        />
        <AlertButton
          type={BUTTON_TYPES.SECONDARY}
          config={secondary_button}
          onClick={handleButtonClick}
        />
      </DialogActions>
    );
  }

  return null;
};

type AlertButtonProps = {
  config?: ButtonConfig;
  onClick: (
    button_type: ErrorModalButtonType,
    action_name?: string | null
  ) => void;
  type: ErrorModalButtonType;
};

const AlertButton: React.FC<AlertButtonProps> = props => {
  const { config, onClick, type } = props;
  if (!config) {
    return null;
  }

  const color = COLOR[type];

  return (
    <Button
      {...(config.props || {})}
      color={color}
      fullWidth={true}
      onClick={() => onClick(type, config.action_name)}
      size="lg"
    >
      {config.label}
    </Button>
  );
};

type AlertDialogStylesProps = Partial<
  Record<"content" | "box" | "title" | "body", string>
>;

type Props = {
  actionHandler?: (
    button_type: ErrorModalButtonType,
    action_name?: string | null | undefined
  ) => void;
  cta_button?: ButtonConfig;
  primary_button?: ButtonConfig;
  secondary_button?: ButtonConfig;
  fullScreen?: boolean;
  classes?: AlertDialogStylesProps;
  classnames_override?: Partial<Record<"message", string>>;
} & AlertTitleProps &
  AlertBodyProps;

export const AlertDialog: React.FC<Props> = props => {
  const {
    actionHandler,
    cta_button,
    message,
    primary_button,
    secondary_button,
    tip,
    title,
    onClose,
    classes: external_classes,
    classnames_override,
    ...others
  } = props;
  const { classes: dialog_styles } = useDialogStyles();
  const classes = external_classes || dialog_styles;

  const handleButtonClick = (
    button_type: ErrorModalButtonType,
    action_name: string | null | undefined
  ) => {
    if (actionHandler) {
      actionHandler(button_type, action_name);
    }
  };

  return (
    <Dialog
      classes={{
        box: classes.box
      }}
      open
      maxWidth="xs"
      fullWidth
      onClose={onClose}
      {...others}
    >
      <AlertTitle
        classes={{
          root: classes.title
        }}
        title={title}
        onClose={onClose}
      />
      <Divider />
      <AlertBody
        classNames={classnames_override?.message}
        message={message}
        tip={tip}
      />
      <AlertFooter
        cta_button={cta_button}
        primary_button={primary_button}
        secondary_button={secondary_button}
        handleButtonClick={handleButtonClick}
      />
    </Dialog>
  );
};

export default AlertDialog;
